// ═══════════════════════════════════════════════════════════════
// Workflow Tables Registry
// Catalogues which tables belong to which module so the workflow
// editor can offer a Module → Table → Field picker for
// the generic record_event_* triggers.
// Uses V2 table/field keys from src/api/tables.ts + fields.ts
// ═══════════════════════════════════════════════════════════════

import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';

export interface WorkflowTableEntry {
  tableKey: string;
  name: keyof typeof FIELDS;
  label: string;
}

export interface WorkflowModuleEntry {
  module: string;
  label: string;
  tables: WorkflowTableEntry[];
}

// Helper — returns null if the name isn't in both TABLES and FIELDS (silently dropped)
function entry(name: keyof typeof FIELDS, label: string): WorkflowTableEntry | null {
  const tableKey = (TABLES as Record<string, string>)[name as string];
  if (!tableKey || !FIELDS[name]) return null;
  return { tableKey, name, label };
}

function compact(arr: (WorkflowTableEntry | null)[]): WorkflowTableEntry[] {
  return arr.filter((e): e is WorkflowTableEntry => e !== null);
}

export const WORKFLOW_TABLE_REGISTRY: WorkflowModuleEntry[] = [
  {
    module: 'incidents',
    label: 'Incidents',
    tables: compact([
      entry('incidents',   'Incidents'),
      entry('activityLog', 'Activity Log'),
    ]),
  },
  {
    module: 'shelter',
    label: 'Shelter',
    tables: compact([
      entry('shelters',           'Shelters'),
      entry('survivors',          'Survivors'),
      entry('shelterFamilies',    'Families'),
      entry('shelterPets',        'Pets'),
      entry('shelterVehicles',    'Vehicles'),
      entry('shelterMedicalLog',  'Medical Log Entries'),
      entry('shelterTransfers',   'Shelter Transfers'),
      entry('shelterIncidents',   'Shelter Incidents'),
      entry('shelterReferrals',   'Referrals'),
      entry('shelterSupplies',    'Supplies'),
      entry('shelterReunification', 'Reunification Requests'),
    ]),
  },
  {
    module: 'intake',
    label: 'Intake',
    tables: compact([
      entry('individuals',   'Individuals'),
      entry('propertyItems', 'Lost Property Items'),
    ]),
  },
  {
    module: 'impact',
    label: 'Impact Assessment',
    tables: compact([
      entry('impactAssessments', 'Damage Assessments'),
      entry('impactPhotos',      'Assessment Photos'),
      entry('impactSelfReports', 'Self-Reports'),
    ]),
  },
  {
    module: 'debris',
    label: 'Debris ADMS',
    tables: compact([
      entry('debrisZones',        'Debris Zones'),
      entry('debrisContractors',  'Contractors'),
      entry('debrisCrews',        'Crews'),
      entry('debrisTrucks',       'Trucks'),
      entry('debrisLoadTickets',  'Load Tickets'),
      entry('debrisDisposalSites','Disposal Sites'),
      entry('debrisInvoices',     'Invoices'),
      entry('debrisMonitors',     'Monitors'),
      entry('debrisRightOfEntry', 'Right-of-Entry Agreements'),
    ]),
  },
  {
    module: 'mainstreet',
    label: 'MainStreet (Business Recovery)',
    tables: compact([
      entry('msOrganizations',   'Organizations'),
      entry('msContacts',        'Contacts'),
      entry('msFacilities',      'Facilities'),
      entry('msStatusChecks',    'Status Checks'),
      entry('msStatusResponses', 'Status Responses'),
      entry('msDamageReports',   'Damage Reports'),
      entry('msReentryAuths',    'Re-Entry Authorizations'),
      entry('msReentryPasses',   'Re-Entry Passes'),
    ]),
  },
  {
    module: 'volunteer',
    label: 'VolunteerOS',
    tables: compact([
      entry('volunteers',         'Volunteers'),
      entry('volunteerEvents',    'Volunteer Events'),
      entry('volunteerAssignments','Volunteer Assignments'),
    ]),
  },
  {
    module: 'handoff',
    label: 'Handoff',
    tables: compact([
      entry('handoffEvents',      'Handoff Events'),
      entry('handoffNotes',       'Handoff Notes'),
      entry('handoffPositions',   'ICS Positions'),
      entry('handoffActionItems', 'Action Items'),
    ]),
  },
  {
    module: 'hotwash',
    label: 'HotWash',
    tables: compact([
      entry('observations', 'HotWash Observations'),
    ]),
  },
  {
    module: 'geoops',
    label: 'GeoOps',
    tables: compact([
      entry('geoOpsZones', 'GeoOps Zones'),
    ]),
  },
  {
    module: 'pagrants',
    label: 'PA Grants',
    tables: compact([
      entry('paOrganizations',         'PA Organizations'),
      entry('paProjects',              'PA Projects'),
      entry('paDamageInventory',       'PA Damage Inventory'),
      entry('paProjectCosts',          'PA Project Costs'),
      entry('paForceAccountLabor',     'Force Account Labor'),
      entry('paForceAccountEquipment', 'Force Account Equipment'),
      entry('paMaterials',             'PA Materials'),
      entry('paContracts',             'PA Contracts'),
      entry('paTimeExtensions',        'Time Extension Requests'),
      entry('paReimbursementRequests', 'Reimbursement Requests'),
    ]),
  },
  {
    module: 'pace',
    label: 'PACE Communications',
    tables: compact([
      entry('paceProfiles',      'PACE Profiles'),
      entry('callDowns',         'Call-Downs'),
      entry('callDownResponses', 'Call-Down Responses'),
      entry('pacePlans',         'PACE Plans'),
    ]),
  },
  {
    module: 'rollcall',
    label: 'Roll Call',
    tables: compact([
      entry('rcPersonnel', 'Personnel'),
      entry('rcEvents',    'Roll Call Events'),
      entry('rcCheckins',  'Check-Ins'),
    ]),
  },
  {
    module: 'battlerhythm',
    label: 'Battle Rhythm',
    tables: compact([
      entry('brMeta',   'Battle Rhythm Activations'),
      entry('brEvents', 'Battle Rhythm Events'),
    ]),
  },
  {
    module: 'blackboard',
    label: 'Blackboard',
    tables: compact([
      entry('bbTrainings',       'Trainings'),
      entry('bbTrainingRecords', 'Training Records'),
      entry('bbClassrooms',      'Classrooms'),
      entry('bbCertificates',    'Certificates'),
      entry('bbPositionPlans',   'Position Plans'),
    ]),
  },
  {
    module: 'forms',
    label: 'Forms',
    tables: compact([
      entry('formTemplates', 'Form Templates'),
      entry('formResponses', 'Form Responses'),
    ]),
  },
  {
    module: 'filelibrary',
    label: 'File Library',
    tables: compact([
      entry('flDocuments', 'Documents'),
    ]),
  },
];

// ── Reverse lookups ────────────────────────────────────────────

const TABLE_KEY_TO_ENTRY: Map<string, WorkflowTableEntry & { module: string; moduleLabel: string }> = new Map();
for (const m of WORKFLOW_TABLE_REGISTRY) {
  for (const t of m.tables) {
    TABLE_KEY_TO_ENTRY.set(t.tableKey, { ...t, module: m.module, moduleLabel: m.label });
  }
}

/** Look up a table entry (with module info) by table key */
export function getTableByKey(tableKey: string) {
  return TABLE_KEY_TO_ENTRY.get(tableKey);
}

/** Get the FIELDS object for a given table key, or null if not registered */
export function getFieldsForTable(tableKey: string): Record<string, string> | null {
  const e = TABLE_KEY_TO_ENTRY.get(tableKey);
  if (!e) return null;
  const f = (FIELDS as Record<string, Record<string, string>>)[e.name as string];
  return f || null;
}

/** Get a list of field names available on the given table */
export function getFieldNamesForTable(tableKey: string): string[] {
  const fields = getFieldsForTable(tableKey);
  return fields ? Object.keys(fields) : [];
}

/** Tables for a given module */
export function getTablesForModule(module: string): WorkflowTableEntry[] {
  return WORKFLOW_TABLE_REGISTRY.find(m => m.module === module)?.tables ?? [];
}

/** Module options for dropdown */
export const WORKFLOW_MODULE_OPTIONS = WORKFLOW_TABLE_REGISTRY.map(m => ({
  value: m.module,
  label: m.label,
}));

/** Convert a camelCase field name to a Title-Case label */
export function fieldLabel(fieldName: string): string {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, c => c.toUpperCase())
    .trim();
}
